import React, {
  forwardRef,
  ForwardRefExoticComponent,
  ReactNode,
  RefAttributes,
  RefObject,
  TextareaHTMLAttributes,
  useEffect,
} from 'react';

import { useRows } from '~/components/chat';
import { useChatInputStore } from '~/stores/use-chat-input/useChatInput';
import { Statistics } from '~/views/alfred/document-search/document-sources-view/document-sources-table/use-document-sources-table-store/useDocumentSourcesTableStore';
import { useDocumentSearch } from '~/views/alfred/document-search/use-document-search/useDocumentSearch';
import { useChatNavigation } from '~/views/alfred/use-chat-navigation/useChatNavigation';

import { ChatInputContextHeader } from '../chat-input-context-header/ChatInputContextHeader';
import { AnalysisModeDropdown } from './AnalysisModeDropdown';
import * as S from './ChatDocumentContextInput.styles';

export interface ChatDocumentContextInputProps
  extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  children?: ReactNode;
  inputRef?: RefObject<HTMLTextAreaElement | null>;
  isMessagePending: boolean;
  contextCounts?: {
    documentsCount: number;
    subTypeCount: number;
    statistics?: Statistics;
  };
}

export const ChatDocumentContextInput = forwardRef<
  HTMLDivElement,
  ChatDocumentContextInputProps
>(
  (
    {
      children,
      onChange,
      disabled = false,
      value,
      isMessagePending,
      inputRef,
      contextCounts,
      ...rest
    },
    ref
  ) => {
    const { rows, updateRows } = useRows({
      value: value,
      textAreaRef: inputRef,
    });
    const { isSubmitButtonPinned, setSubmitButtonPinned } = useChatInputStore(
      state => ({
        isSubmitButtonPinned: state.isSubmitButtonPinned,
        setSubmitButtonPinned: state.setSubmitButtonPinned,
      })
    );

    // If the text wraps in 3 lines, the button placement will change
    useEffect(() => {
      if (!inputRef?.current || document.activeElement !== inputRef.current) {
        return;
      }

      if (!isSubmitButtonPinned && rows >= 4) {
        setSubmitButtonPinned(true);
      } else if (isSubmitButtonPinned && rows <= 2) {
        setSubmitButtonPinned(false);
      }
    }, [rows, isSubmitButtonPinned, setSubmitButtonPinned, inputRef]);

    const { mode } = useChatNavigation();
    const hasActiveDocumentMessage = useDocumentSearch(
      state => !state.isMessagePending && !!state.messages.length
    );

    const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      updateRows(event);
      onChange?.(event);
    };

    const onKeyDown: React.KeyboardEventHandler<HTMLTextAreaElement> = e => {
      if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
        e.stopPropagation();
      } else {
        rest.onKeyDown?.(e);
      }
    };

    return (
      <S.ChatInputFieldWrapper ref={ref} $isMessagePending={isMessagePending}>
        {mode === 'document' && hasActiveDocumentMessage && (
          <ChatInputContextHeader contextCounts={contextCounts} />
        )}
        <S.ChatInputFieldContainer>
          <S.TextAreaWrapper $submitButtonPinned={isSubmitButtonPinned}>
            <S.TextareaElement
              id="dropdown-trigger"
              as="textarea"
              ref={inputRef}
              onChange={handleChange}
              rows={rows}
              disabled={disabled}
              value={value}
              {...rest}
              data-testid="chat-input"
              {...(mode === 'research' ? { onKeyDown } : {})}
            />
            {mode !== 'research' && children}
          </S.TextAreaWrapper>
          {mode === 'research' && (
            <S.AlfredTools>
              <AnalysisModeDropdown isMessagePending={isMessagePending} />
              {children}
            </S.AlfredTools>
          )}
        </S.ChatInputFieldContainer>
      </S.ChatInputFieldWrapper>
    );
  }
) as ForwardRefExoticComponent<
  ChatDocumentContextInputProps & RefAttributes<HTMLTextAreaElement>
> & {
  Styled: typeof S;
};

ChatDocumentContextInput.Styled = S;
